// User provisioning. Admin only, per Section 7.5.
//
// Pattern B, a full table at 1760px. Six columns need the room, and the add
// form moved to its own Pattern C route rather than being crushed into an
// aside.
//
// Adding someone here does not create a Google account. It permits one that
// already exists, since sign-in needs both a company Google account and an
// active row in this list.

import { useCallback, useEffect, useState } from 'react'
import { listUsers, listFranchisees, updateUser, removeUser, importUsersFile } from '../api.js'
import { useApiForm } from '../useApiForm.js'
import { useAuth } from '../auth.jsx'
import { ROLES, ROLE_LABEL } from '../../../shared/permissions.js'
import {
  Button,
  ButtonLink,
  Notice,
  Panel,
  Pill,
  Select,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '../components/ui/index.js'
import { useConfirm } from '../confirm.jsx'
import ImportCard from '../ImportCard.jsx'

// Last sign-in, or Never: the one date on this page with a word for unset.
function formatLastLogin(value) {
  if (!value) return 'Never'
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function UserRow({ user, franchisees, isSelf, onChanged }) {
  const confirm = useConfirm()
  const { saving, error, submit } = useApiForm()

  async function change(changes) {
    const result = await submit(() => updateUser(user.id, changes))
    if (result) onChanged()
  }

  async function remove() {
    const confirmed = await confirm(
      `Remove ${user.displayName}? They lose access immediately. Their history is kept.`,
    )
    if (!confirmed) return
    const result = await submit(() => removeUser(user.id))
    if (result) onChanged()
  }

  return (
    <>
      <Tr className={user.isActive ? undefined : 'is-dimmed'}>
        <Td>
          <div className="row-primary">{user.displayName}</div>
          <div className="ui-cell-meta">{user.email}</div>
        </Td>
        <Td>
          {/* Your own role is locked. A disabled dropdown is noise. */}
          {isSelf ? (
            ROLE_LABEL[user.role]
          ) : (
            <Select
              value={user.role}
              disabled={saving}
              onChange={(role) => change({ role })}
              ariaLabel={`Role for ${user.displayName}`}
              options={ROLES.map((role) => ({ value: role.value, label: role.label }))}
            />
          )}
        </Td>
        <Td>
          {user.role === 'FRANCHISEE' ? (
            <>
              {user.franchisees.map((clinic) => (
                <div key={clinic.id} className="ui-cell-meta">
                  {clinic.name}{' '}
                  <Button
                    variant="ghost"
                    disabled={saving || user.franchisees.length === 1}
                    title={
                      user.franchisees.length === 1
                        ? 'A Franchisee user needs at least one clinic'
                        : `Remove access to ${clinic.name}`
                    }
                    onClick={() =>
                      change({
                        franchiseeIds: user.franchisees
                          .filter((linked) => linked.id !== clinic.id)
                          .map((linked) => linked.id),
                      })
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Select
                value=""
                disabled={saving}
                onChange={(franchiseeId) =>
                  franchiseeId &&
                  change({
                    franchiseeIds: [...user.franchisees.map((linked) => linked.id), franchiseeId],
                  })
                }
                ariaLabel={`Add a clinic for ${user.displayName}`}
                options={[
                  {
                    value: '',
                    label: user.franchisees.length ? 'Add a clinic' : 'Choose a clinic',
                  },
                  ...franchisees
                    .filter((clinic) => !user.franchisees.some((linked) => linked.id === clinic.id))
                    .map((clinic) => ({ value: clinic.id, label: clinic.name })),
                ]}
              />
            </>
          ) : (
            <span className="muted">Not applicable</span>
          )}
        </Td>
        <Td>
          <Pill tone={user.isActive ? 'ok' : 'neutral'}>
            {user.isActive ? 'Active' : 'Inactive'}
          </Pill>
        </Td>
        <Td numeric={Boolean(user.lastLoginAt)}>{formatLastLogin(user.lastLoginAt)}</Td>
        <Td numeric>
          <Button disabled={isSelf || saving} onClick={() => change({ isActive: !user.isActive })}>
            {user.isActive ? 'Deactivate' : 'Activate'}
          </Button>{' '}
          <Button variant="ghost" disabled={isSelf || saving} onClick={remove}>
            Remove
          </Button>
        </Td>
      </Tr>
      {error ? (
        <Tr>
          <Td colSpan={6}>
            <span className="row-error">{error}</span>
          </Td>
        </Tr>
      ) : null}
    </>
  )
}

export default function Users() {
  const { user: currentUser } = useAuth()
  const [users, setUsers] = useState(null)
  const [franchisees, setFranchisees] = useState([])
  const [importing, setImporting] = useState(false)
  const [loadError, setLoadError] = useState(null)

  const load = useCallback(async () => {
    try {
      const [userData, franchiseeData] = await Promise.all([listUsers(), listFranchisees()])
      setUsers(userData.users)
      setFranchisees(franchiseeData.franchisees)
      setLoadError(null)
    } catch (caught) {
      setLoadError(caught.message)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  if (loadError) {
    return <Notice tone="bad">{loadError}</Notice>
  }
  if (!users) return <div className="center-note">Loading</div>

  const activeCount = users.filter((user) => user.isActive).length

  return (
    <>
      {/* Secondary controls go in a toolbar rather than an aside, per Pattern B. */}
      <div className="toolbar">
        <span className="muted">
          {activeCount} active of {users.length}
        </span>
        <span className="spacer" />
        <Button variant="ghost" onClick={() => setImporting((open) => !open)}>
          Import
        </Button>
        <ButtonLink variant="primary" to="/admin/users/new">
          Add user
        </ButtonLink>
      </div>

      {importing ? (
        <ImportCard
          id="users-file"
          title="Import users"
          description="CSV or Excel. Columns: first_name, last_name, email, role and clinic_name. Role is Admin or Franchisee; a Franchisee row names its clinic exactly as the Owners tab spells it, several separated by semicolons. A file with any bad row is refused whole, and an email already provisioned is refused rather than overwritten."
          request={importUsersFile}
          describe={(result) => `${result.users.length} users imported.`}
          onImported={load}
          onClose={() => setImporting(false)}
        />
      ) : null}

      <Panel
        title="Provisioned users"
        support="Your own role and access are locked, and so is the last remaining admin, so nobody can remove their own way back in."
        flush
      >
        <Table>
          <Thead>
            <Tr>
              <Th>Person</Th>
              <Th>Role</Th>
              <Th>Clinic name</Th>
              <Th>Status</Th>
              <Th numeric>Last sign-in</Th>
              <Th numeric>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                franchisees={franchisees}
                isSelf={user.id === currentUser.id}
                onChanged={load}
              />
            ))}
          </Tbody>
        </Table>
      </Panel>
    </>
  )
}
